import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { List } from '@avslack/shared';
import {
  createList,
  createListItem,
  deleteList,
  deleteListItem,
  getLists,
  updateList,
  updateListItem,
} from '../lib/api';

export const listsKey = (channelId: string) => ['lists', channelId] as const;

export function useLists(channelId: string, enabled = true) {
  return useQuery({
    queryKey: listsKey(channelId),
    queryFn: () => getLists(channelId),
    enabled: enabled && Boolean(channelId),
    staleTime: 30_000,
  });
}

/**
 * Minden muvelet a TELJES listaval ter vissza.
 *
 * Egy tetel pipalasa a lista osszegzo szamait is elmozditja ("3/8 kesz"), es a
 * sorrend is valtozhat. A teljes lista visszaadasa egyetlen valaszban rendezi
 * ezt - kulon lekerdezes nelkul, es ugy, hogy sosem lehet felig frissitett
 * allapot a kepernyon.
 */
function apply(queryClient: QueryClient, channelId: string, list: List): void {
  queryClient.setQueryData<List[]>(listsKey(channelId), (current) => {
    if (!current) return [list];
    const index = current.findIndex((item) => item.id === list.id);
    if (index === -1) return [list, ...current];
    const next = [...current];
    next[index] = list;
    return next;
  });
}

export function upsertList(queryClient: QueryClient, list: List): void {
  apply(queryClient, list.channelId, list);
}

export function removeList(queryClient: QueryClient, channelId: string, listId: string): void {
  queryClient.setQueryData<List[]>(listsKey(channelId), (current) =>
    (current ?? []).filter((item) => item.id !== listId),
  );
}

export function useCreateList(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; description?: string }) => createList(channelId, input),
    onSuccess: (list) => apply(queryClient, channelId, list),
  });
}

export function useUpdateList(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      listId,
      patch,
    }: {
      listId: string;
      patch: { title?: string; description?: string };
    }) => updateList(listId, patch),
    onSuccess: (list) => apply(queryClient, channelId, list),
  });
}

export function useDeleteList(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteList,
    onSuccess: (_data, listId) => removeList(queryClient, channelId, listId),
  });
}

export function useCreateListItem(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      listId,
      input,
    }: {
      listId: string;
      input: { text?: string; assigneeId?: string | null; dueDate?: string | null };
    }) => createListItem(listId, input),
    onSuccess: (list) => apply(queryClient, channelId, list),
  });
}

export function useUpdateListItem(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      patch,
    }: {
      itemId: string;
      patch: {
        text?: string;
        isDone?: boolean;
        assigneeId?: string | null;
        dueDate?: string | null;
      };
    }) => updateListItem(itemId, patch),
    onSuccess: (list) => apply(queryClient, channelId, list),
  });
}

export function useDeleteListItem(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteListItem,
    onSuccess: (list) => apply(queryClient, channelId, list),
  });
}
